import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Box, Button, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import dayjs, { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import CustomModal from "../../../components/modal/CustomModal";
import { InputReactHookForm } from "../../../components/input/InputReactHookForm";
import CustomSelect, { MenuData } from "../../../components/input/CustomSelect";
import CustomDatePicker from "../../../components/input/CustomDatePicker";
import { common, financedEmissions } from "../../../i18n";
import { BorrowerLoan } from "../../../models/dto/cat15/cat15.types";

interface Props {
  open: boolean;
  onClose: () => void;
  loan?: BorrowerLoan;
  onSubmit: (values: AddSnapshotForm) => void;
}

export interface AddSnapshotForm {
  year: string;
  outstanding: string;
  /** YYYY-MM-DD, optional */
  asOfDate: string;
}

/** How many years back the Year picker offers (minus the ones a loan already
 *  has — the BE rejects a duplicate loan-year with 409). */
const YEAR_SPAN = 7;

/** Add Year-End Snapshot modal — Figma node 6097:51182. */
const AddSnapshotModal = ({ open, onClose, loan, onSubmit }: Props) => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });

  const { control, handleSubmit, watch, setValue, reset } =
    useForm<AddSnapshotForm>({
      defaultValues: { year: "", outstanding: "", asOfDate: "" },
    });
  const [asOf, setAsOf] = useState<Dayjs | null>(null);

  // Years not already snapshotted on this loan, newest first.
  const yearOptions: MenuData[] = useMemo(() => {
    const taken = new Set((loan?.snapshots ?? []).map((s) => s.year));
    const thisYear = new Date().getFullYear();
    return Array.from({ length: YEAR_SPAN }, (_, i) => thisYear - i)
      .filter((y) => !taken.has(y))
      .map((y) => ({ value: String(y), nameEN: String(y), nameTH: String(y) }));
  }, [loan]);

  // Reset the form whenever the modal (re)opens for a loan.
  useEffect(() => {
    if (open) {
      reset({ year: "", outstanding: "", asOfDate: "" });
      setAsOf(null);
    }
  }, [open, loan, reset]);

  const year = watch("year");

  const submit = (values: AddSnapshotForm) => {
    onSubmit({
      ...values,
      asOfDate: asOf ? asOf.format("YYYY-MM-DD") : "",
    });
    onClose();
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      maxWidth="588px"
      header={
        <Box>
          <Typography variant="h1">
            + {t("Add-Year-End-Snapshot")}
          </Typography>
          {loan && (
            <Typography variant="body2" color="var(--gray9)">
              {loan.loanRef}
            </Typography>
          )}
        </Box>
      }
      body={
        <Box display="flex" flexDirection="column" gap="20px">
          {/* Info banner — Figma green3 tint */}
          <Box
            display="flex"
            alignItems="center"
            gap="8px"
            bgcolor="var(--green3)"
            borderRadius="8px"
            p="8px 12px"
          >
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              width={24}
              height={24}
              flexShrink={0}
              color="var(--green)"
            >
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
            </Box>
            <Typography fontSize="12px" lineHeight="16px" color="var(--gray1)">
              {t("Snapshot-Bank-Note")}
            </Typography>
          </Box>

          <CustomSelect
            required
            label={t("Col-Year")}
            value={year}
            placeholder={t("Select-Year")}
            listMenuItem={yearOptions}
            handleValeOnChange={(e) => setValue("year", e.target.value)}
          />

          <InputReactHookForm
            required
            name="outstanding"
            control={control}
            label={t("Outstanding-Amount-THB")}
            placeholder="0.00"
            rules={{ required: t("Outstanding-Amount-THB") }}
          />

          <CustomDatePicker
            label={t("Col-As-Of")}
            info={t("Col-As-Of")}
            views={["year", "month", "day"]}
            format="DD/MM/YYYY"
            value={asOf}
            defaultDate={null}
            disableFuture
            handleOnChange={(d: Dayjs | null) => setAsOf(d ? dayjs(d) : null)}
          />

          <Box display="flex" justifyContent="flex-end" gap="12px">
            <Button variant="outlined" color="secondary" onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              disabled={!year}
              onClick={handleSubmit(submit)}
            >
              {t("Save-Snapshot")}
            </Button>
          </Box>
        </Box>
      }
    />
  );
};

export default AddSnapshotModal;
